import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { MarkdownField } from '../../../../components/MarkdownField/MarkdownField';
import type { FaqEntry, FaqLink, LinkType, CreateFaqEntryInput, UpdateFaqEntryInput, EntryStatus } from '../../types';

interface EntryFormProps {
  open: boolean;
  entry: FaqEntry | null; // null = create mode
  sectionName: string;
  categoryName: string;
  onClose: () => void;
  onSave: (data: CreateFaqEntryInput | UpdateFaqEntryInput) => Promise<void>;
}

const EMPTY_LINK: FaqLink = { label: '', url: '', type: 'workflow' };

export function EntryForm({ open, entry, sectionName, categoryName, onClose, onSave }: EntryFormProps) {
  const [slug, setSlug] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [answerTab, setAnswerTab] = useState<0 | 1>(0);
  const [status, setStatus] = useState<EntryStatus>('published');
  const [keywordsRaw, setKeywordsRaw] = useState('');
  const [links, setLinks] = useState<FaqLink[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSlug(entry?.slug ?? '');
      setQuestion(entry?.question ?? '');
      setAnswer(entry?.answer ?? '');
      setAnswerTab(0);
      setStatus(entry?.status ?? 'published');
      setKeywordsRaw(entry?.keywords.join(', ') ?? '');
      setLinks(entry?.links.length ? [...entry.links] : []);
      setError('');
    }
  }, [open, entry]);

  const handleAddLink = () => setLinks(prev => [...prev, { ...EMPTY_LINK }]);
  const handleRemoveLink = (i: number) => setLinks(prev => prev.filter((_, idx) => idx !== i));
  const handleLinkChange = (i: number, field: keyof FaqLink, value: string) => {
    setLinks(prev => prev.map((a, idx) => idx === i ? { ...a, [field]: value } : a));
  };

  const handleSave = async () => {
    if (!question.trim()) { setError('Question is required'); return; }
    if (!answer.trim()) { setError('Answer is required'); return; }
    if (entry && slug.trim() === '') { setError('Slug cannot be empty'); return; }
    if (entry && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim())) {
      setError('Slug must contain only lowercase letters, numbers, and hyphens');
      return;
    }

    const keywords = keywordsRaw
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    const validLinks = links.filter(a => a.label.trim() && a.url.trim());

    setSaving(true);
    try {
      const data = entry
        ? { slug: slug.trim(), question: question.trim(), answer: answer.trim(), status, keywords, links: validLinks }
        : { question: question.trim(), answer: answer.trim(), status, keywords, links: validLinks };
      await onSave(data);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{entry ? 'Edit Q&A Entry' : 'Add Q&A Entry'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Box fontSize={13} color="text.secondary">
            <strong>{sectionName}</strong> / {categoryName}
          </Box>

          {entry && (
            <TextField
              label="Slug"
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-'))}
              fullWidth
              helperText="URL identifier for this entry. Changing this will break any existing links pointing to it."
            />
          )}

          <TextField
            label="Question"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            fullWidth
            required
          />

          <MarkdownField
            label="Answer"
            value={answer}
            onChange={setAnswer}
            tab={answerTab}
            onTabChange={setAnswerTab}
            required
            error={!answer.trim() && error === 'Answer is required' ? error : undefined}
            minHeight={160}
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={e => setStatus(e.target.value as EntryStatus)}
            >
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Keywords"
            value={keywordsRaw}
            onChange={e => setKeywordsRaw(e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Enter keywords separated by commas — e.g. github, repository, create"
          />

          {/* Links */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight={600}>Links</Typography>
              <Button startIcon={<AddIcon />} size="small" onClick={handleAddLink}>
                Add link
              </Button>
            </Box>
            {links.map((link, i) => (
              <Box key={i} display="flex" gap={1} mb={1} alignItems="flex-start">
                <TextField
                  label="Label"
                  value={link.label}
                  onChange={e => handleLinkChange(i, 'label', e.target.value)}
                  size="small"
                  style={{ flex: 2 }}
                />
                <TextField
                  label="URL"
                  value={link.url}
                  onChange={e => handleLinkChange(i, 'url', e.target.value)}
                  size="small"
                  style={{ flex: 4 }}
                />
                <FormControl size="small" style={{ flex: 1.5 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={link.type}
                    label="Type"
                    onChange={e => handleLinkChange(i, 'type', e.target.value as LinkType)}
                  >
                    <MenuItem value="workflow">Workflow</MenuItem>
                    <MenuItem value="docs">Docs</MenuItem>
                    <MenuItem value="external">External</MenuItem>
                  </Select>
                </FormControl>
                <IconButton size="small" onClick={() => handleRemoveLink(i)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {error && (
            <Box color="error.main" fontSize={13}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
